import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
} from 'react-native';
import { Image } from 'expo-image';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Fonts, Spacing, Radii, Typography } from '../constants';
import { imageService } from '../services/images';
import { ConditionBadge } from './ConditionBadge';

// Image sizes
const IMAGE_SIZE_COLLAPSED = 44;
const IMAGE_SIZE_EXPANDED = 72;

interface ItemInfoPanelProps {
  item: {
    title: string;
    price: number;
    size?: string;
    condition?: string;
    description?: string;
    image_urls?: string[];
    club?: {
      name: string;
    };
  };
  onItemPress: () => void;
  defaultExpanded?: boolean;
}

export const ItemInfoPanel: React.FC<ItemInfoPanelProps> = ({
  item,
  onItemPress,
  defaultExpanded = true,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Single animation value for all animations
  const animValue = useRef(new Animated.Value(defaultExpanded ? 1 : 0)).current;

  const imageUrl = item.image_urls && item.image_urls.length > 0
    ? imageService.getThumbnailUrl(item.image_urls[0])
    : null;

  const toggleExpanded = () => {
    const toValue = expanded ? 0 : 1;

    Animated.timing(animValue, {
      toValue,
      duration: 250,
      useNativeDriver: false, // Need false for layout animations (width/height)
    }).start();

    setExpanded(!expanded);
  };

  // Interpolate all animations from single value
  const chevronRotation = animValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const imageSize = animValue.interpolate({
    inputRange: [0, 1],
    outputRange: [IMAGE_SIZE_COLLAPSED, IMAGE_SIZE_EXPANDED],
  });

  const expandedOpacity = animValue.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, 0, 1],
  });

  const expandedMaxHeight = animValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 100], // Increased for additional content
  });

  const hasExpandedContent = item.size || item.condition || item.club?.name || item.description;

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={styles.header}
        onPress={toggleExpanded}
        activeOpacity={0.7}
      >
        {/* Animated Thumbnail */}
        <Animated.View
          style={[
            styles.thumbnailContainer,
            {
              width: imageSize,
              height: imageSize,
            },
          ]}
        >
          {imageUrl ? (
            <Image
              source={{ uri: imageUrl }}
              style={styles.thumbnail}
              contentFit="cover"
              transition={200}
              cachePolicy="memory-disk"
            />
          ) : (
            <View style={styles.thumbnailPlaceholder}>
              <Text style={styles.thumbnailPlaceholderText}>
                {t('components.itemCard.noImage')}
              </Text>
            </View>
          )}
        </Animated.View>

        {/* Title and Price */}
        <View style={styles.headerContent}>
          <Text style={styles.title} numberOfLines={1}>
            {item.title}
          </Text>
        </View>

        <Text style={styles.price}>€{item.price.toFixed(2)}</Text>

        {/* Animated Chevron */}
        <Animated.View style={[styles.chevron, { transform: [{ rotate: chevronRotation }] }]}>
          <Ionicons name="chevron-down-outline" size={18} color={Colors.textLight} />
        </Animated.View>
      </TouchableOpacity>

      {/* Expanded Content - additional item details */}
      {hasExpandedContent && (
        <Animated.View
          style={[
            styles.expandedWrapper,
            {
              opacity: expandedOpacity,
              maxHeight: expandedMaxHeight,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.expandedContent}
            onPress={onItemPress}
            activeOpacity={0.8}
            disabled={!expanded}
          >
            {/* Club name */}
            {item.club?.name && (
              <Text style={styles.clubName}>{item.club.name}</Text>
            )}

            {/* Size and Condition */}
            {(item.size || item.condition) && (
              <View style={styles.detailsRow}>
                {item.size && (
                  <Text style={styles.detailText}>
                    {t('components.itemCard.size')}: {item.size}
                  </Text>
                )}
                {item.size && item.condition && (
                  <Text style={styles.detailSeparator}>·</Text>
                )}
                {item.condition && (
                  <ConditionBadge condition={item.condition} compact />
                )}
              </View>
            )}

            {/* Description (1 line max) */}
            {item.description && (
              <Text style={styles.description} numberOfLines={1}>
                {item.description}
              </Text>
            )}
          </TouchableOpacity>
        </Animated.View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },

  // Header (collapsed state always visible)
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: Spacing.base,
    gap: Spacing.md,
  },
  thumbnailContainer: {
    borderRadius: Radii.thumb,
    overflow: 'hidden',
    backgroundColor: Colors.card,
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  thumbnailPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.card,
  },
  thumbnailPlaceholderText: {
    fontFamily: Fonts.regular,
    fontSize: 8,
    color: Colors.border,
    textAlign: 'center',
  },
  headerContent: {
    flex: 1,
  },
  title: {
    ...Typography.cardTitle,
    fontSize: 14,
    color: Colors.text,
  },
  price: {
    ...Typography.price,
    color: Colors.textDark,
  },
  chevron: {
    marginLeft: Spacing.xs,
  },

  // Expanded content
  expandedWrapper: {
    overflow: 'hidden',
  },
  expandedContent: {
    paddingHorizontal: Spacing.base,
    paddingBottom: Spacing.sm,
  },
  detailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
  },
  detailText: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    lineHeight: 16,
    color: Colors.textMedium,
  },
  detailSeparator: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    lineHeight: 16,
    color: Colors.textMedium,
    marginHorizontal: Spacing.xs,
  },
  clubName: {
    fontFamily: Fonts.semiBold,
    fontSize: 12,
    lineHeight: 16,
    color: Colors.textMedium,
    marginBottom: Spacing.xs,
  },
  description: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    lineHeight: 16,
    color: Colors.textMedium,
    marginTop: Spacing.xs,
  },
});
